import { type CustomerAgeingData } from "@/types/ageing-analysis";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpDown,
} from "lucide-react";
import { format } from "date-fns";

type SortField = "customerName" | "totalOutstanding" | "current" | "days31_60" | "days61_90" | "days91_120" | "over120" | "lastContactDate" | "riskLevel" | "collectionStrategy";
type SortDirection = "asc" | "desc" | null;

function SortableHeader({ field, children, className, onSort }: Readonly<{ field: SortField; children: React.ReactNode; className?: string; onSort: (field: SortField) => void }>) {
  return (
    <TableHead className={className}>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 data-[state=open]:bg-accent"
        onClick={() => onSort(field)}
      >
        {children}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    </TableHead>
  );
}

interface AgeingAnalysisTableProps {
  readonly customers: CustomerAgeingData[];
  readonly selectedCustomerIds: Set<string>;
  readonly visibleColumns: Record<string, boolean>;
  readonly sortField: SortField | null;
  readonly sortDirection: SortDirection;
  readonly onSort: (field: SortField) => void;
  readonly onSelectAll: (checked: boolean) => void;
  readonly onSelectCustomer: (customerId: string, checked: boolean) => void;
  readonly onLogCall: (customer: CustomerAgeingData) => void;
  readonly onSendEmail: (customer: CustomerAgeingData) => void;
  readonly onCreatePromise: (customer: CustomerAgeingData) => void;
  readonly onAssignClerk: (customer: CustomerAgeingData) => void;
  readonly onViewCustomer: (customer: CustomerAgeingData) => void;
  readonly formatCurrency: (amount: number) => string;
  readonly getRiskBadgeColor: (risk: string) => string;
}

export function AgeingAnalysisTable({
  customers,
  selectedCustomerIds: _selectedCustomerIds,
  visibleColumns,
  sortField: _sortField,
  sortDirection: _sortDirection,
  onSort,
  onSelectAll: _onSelectAll,
  onSelectCustomer: _onSelectCustomer,
  onLogCall,
  onSendEmail,
  onCreatePromise,
  onAssignClerk,
  onViewCustomer,
  formatCurrency,
  getRiskBadgeColor,
}: AgeingAnalysisTableProps) {

  if (customers.length === 0) {
    return (
      <div className="border rounded-lg p-12 text-center">
        <p className="text-muted-foreground">No customers found matching your filters</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader field="customerName" onSort={onSort}>Customer</SortableHeader>
            {visibleColumns.creditLimit && (
              <TableHead className="text-right">Credit Limit</TableHead>
            )}
            {visibleColumns.totalOutstanding && (
              <SortableHeader field="totalOutstanding" className="text-right" onSort={onSort}>
                Total Outstanding
              </SortableHeader>
            )}
            {visibleColumns.current && (
              <SortableHeader field="current" className="text-right" onSort={onSort}>
                Current (0-30)
              </SortableHeader>
            )}
            {visibleColumns.days31_60 && (
              <SortableHeader field="days31_60" className="text-right" onSort={onSort}>
                31-60 Days
              </SortableHeader>
            )}
            {visibleColumns.days61_90 && (
              <SortableHeader field="days61_90" className="text-right" onSort={onSort}>
                61-90 Days
              </SortableHeader>
            )}
            {visibleColumns.days91_120 && (
              <SortableHeader field="days91_120" className="text-right" onSort={onSort}>
                91-120 Days
              </SortableHeader>
            )}
            {visibleColumns.over120 && (
              <SortableHeader field="over120" className="text-right" onSort={onSort}>
                Over 120 Days
              </SortableHeader>
            )}
            {visibleColumns.terms && (
              <TableHead>Terms</TableHead>
            )}
            {visibleColumns.lastContactDate && (
              <SortableHeader field="lastContactDate" onSort={onSort}>Last Contact</SortableHeader>
            )}
            {visibleColumns.riskLevel && (
              <SortableHeader field="riskLevel" onSort={onSort}>Risk Level</SortableHeader>
            )}
            {visibleColumns.collectionStrategy && (
              <SortableHeader field="collectionStrategy" onSort={onSort}>Collection Strategy</SortableHeader>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <TableRow key={customer.id} className="hover:bg-muted/50">
              <TableCell>
                <button
                  onClick={() => onViewCustomer(customer)}
                  className="text-left hover:underline"
                >
                  <div className="font-medium text-teal-600">{customer.customerName}</div>
                  <div className="text-sm text-muted-foreground">{customer.accountNumber}</div>
                </button>
              </TableCell>
              {visibleColumns.creditLimit && (
                <TableCell className="text-right">
                  {formatCurrency(customer.creditLimit)}
                </TableCell>
              )}
              {visibleColumns.totalOutstanding && (
                <TableCell className="text-right font-bold">
                  {formatCurrency(customer.totalOutstanding)}
                </TableCell>
              )}
              {visibleColumns.current && (
                <TableCell className="text-right">
                  {formatCurrency(customer.ageing.current)}
                </TableCell>
              )}
              {visibleColumns.days31_60 && (
                <TableCell className="text-right">
                  {formatCurrency(customer.ageing.days31_60)}
                </TableCell>
              )}
              {visibleColumns.days61_90 && (
                <TableCell className="text-right">
                  {formatCurrency(customer.ageing.days61_90)}
                </TableCell>
              )}
              {visibleColumns.days91_120 && (
                <TableCell className="text-right">
                  {formatCurrency(customer.ageing.days91_120)}
                </TableCell>
              )}
              {visibleColumns.over120 && (
                <TableCell className="text-right">
                  {formatCurrency(customer.ageing.over120)}
                </TableCell>
              )}
              {visibleColumns.terms && (
                <TableCell>
                  {customer.terms}
                </TableCell>
              )}
              {visibleColumns.lastContactDate && (
                <TableCell>
                  {format(new Date(customer.lastContactDate), "MMM dd, yyyy")}
                </TableCell>
              )}
              {visibleColumns.riskLevel && (
                <TableCell>
                  <Badge className={getRiskBadgeColor(customer.riskLevel)}>
                    {customer.riskLevel.charAt(0).toUpperCase() + customer.riskLevel.slice(1)}
                  </Badge>
                </TableCell>
              )}
              {visibleColumns.collectionStrategy && (
                <TableCell>
                  <Badge variant="outline">{customer.collectionStrategy}</Badge>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
